'use client';

import { c as _c } from "react/compiler-runtime";
import './volunteer-template.scss';
import { usePathname } from 'next/navigation';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { GoogleMapEmbed } from '@sola/ark-core/atoms/google-map-embed/google-map-embed.tsx';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { Prominence, Sizes, Text, Variants } from '@sola/ark-core/atoms/text/text.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { getPublicConfig } from '@sola/config/lib/getters.ts';
import type { FlexibleContentProps } from '@sola/schema/lib/schema.ts';
import { useLiveVolunteerSlotsQuery } from '@sola/skippy-search-sdk/lib/queries.ts';
import type { VolunteerLocationItemDto } from '@sola/skippy-search-sdk/lib/schema.ts';
import { TrackVolunteerViewContent } from '@sola/tracking/components/datalayer.tsx';
import { ShareButtons } from '../../molecules/share-buttons/share-buttons.tsx';
import { AccordionMain } from '../../organisms-cms/accordion/main/accordion-main.tsx';
import { BasicContentDuo } from '../../organisms-cms/basic-content/duo/basic-content-duo.tsx';
import { BasicContentSolo } from '../../organisms-cms/basic-content/solo/basic-content-solo.tsx';
import { ExpandedContentTabbed } from '../../organisms-cms/expanded-content/tabbed/expandedContent-tabbed.tsx';
import { FlexibleSection } from '../../organisms/flexible-section/flexible-section.tsx';
import { VolunteerRegistrationSheet } from '../../organisms/volunteer-registration-sheet/volunteer-registration-sheet.tsx';
import { FSmeta, FSmetaFullBleedLight, FSmetaLight } from '../../utils/section-meta.ts';
import { buildCoordinatorMailtoUrl, buildFilteredRoleItems, buildLocationMedia, buildShareSummary, formatEventDateRange, formatVolunteerAddress } from '../../utils/volunteer-utils.ts';
import { useVolunteerLocationTemplate } from './volunteer-location-template.logic.ts';
export interface VolunteerLocationTemplateProps {
  location: VolunteerLocationItemDto;
  content?: Partial<Record<'roles' | 'faq' | 'questions', FlexibleContentProps>>;
}
export const VolunteerLocationTemplate: React.FC<VolunteerLocationTemplateProps> = t0 => {
  const $ = _c(119);
  const {
    location,
    content
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["volunteers"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = getPublicConfig();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    NEXT_PUBLIC_GOOGLE_MAPS_KEY
  } = t2;
  const pathname = usePathname();
  const {
    isLoggedIn,
    shouldAutoOpen,
    loginUrl
  } = useVolunteerLocationTemplate(pathname);
  const {
    data: liveSlots
  } = useLiveVolunteerSlotsQuery(location.eventId);
  const roleCounts = liveSlots?.roleCounts;
  const openSlots = liveSlots?.totalOpenSlots ?? location.openSlots;
  const {
    lines: addressLines,
    mapQuery,
    mapsUrl
  } = formatVolunteerAddress(location);
  const t3 = content?.roles?.items;
  let t4;
  if ($[2] !== location.roles) {
    t4 = location.roles ?? [];
    $[2] = location.roles;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t) {
    t5 = count => t("volunteers:slotsOpenBadge", {
      count
    });
    $[4] = t;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== roleCounts || $[7] !== t3 || $[8] !== t4 || $[9] !== t5) {
    t6 = buildFilteredRoleItems(t3, roleCounts, t4, t5);
    $[6] = roleCounts;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const filteredRoleItems = t6;
  const [eventFull, setEventFull] = useState(false);
  let t7;
  if ($[11] !== eventFull || $[12] !== isLoggedIn || $[13] !== location || $[14] !== loginUrl || $[15] !== openSlots || $[16] !== t) {
    t7 = autoOpen => {
      if (openSlots <= 0) {
        return null;
      }
      if (isLoggedIn) {
        return <VolunteerRegistrationSheet eventId={location.eventId} location={location} defaultOpen={autoOpen} onEventFull={() => setEventFull(true)} trigger={<ButtonStyle variant="primary" disabled={eventFull}><button disabled={eventFull}>{t("volunteers:register")}</button></ButtonStyle>} />;
      }
      return <ButtonStyle variant="primary"><a href={loginUrl}>{t("volunteers:loginToRegister")}</a></ButtonStyle>;
    };
    $[11] = eventFull;
    $[12] = isLoggedIn;
    $[13] = location;
    $[14] = loginUrl;
    $[15] = openSlots;
    $[16] = t;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const buildRegisterButton = t7;
  let t8;
  if ($[18] !== buildRegisterButton || $[19] !== shouldAutoOpen) {
    t8 = buildRegisterButton(shouldAutoOpen);
    $[18] = buildRegisterButton;
    $[19] = shouldAutoOpen;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const registerButton = t8;
  let t9;
  if ($[21] !== buildRegisterButton) {
    t9 = buildRegisterButton(false);
    $[21] = buildRegisterButton;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const registerButtonSecondary = t9;
  let t10;
  if ($[23] !== content || $[24] !== filteredRoleItems || $[25] !== registerButtonSecondary) {
    t10 = content?.roles ? {
      ...content.roles,
      programmaticButtons: registerButtonSecondary,
      items: filteredRoleItems
    } : content?.roles;
    $[23] = content;
    $[24] = filteredRoleItems;
    $[25] = registerButtonSecondary;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const enrichedRoles = t10;
  const t11 = "VolunteerLocation";
  let t12;
  if ($[27] !== location) {
    t12 = <TrackVolunteerViewContent location={location} />;
    $[27] = location;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      ...FSmeta
    };
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== location) {
    t14 = buildLocationMedia(location);
    $[30] = location;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t14) {
    t15 = <FlexibleSection meta={t13} children1={<BasicContentDuo media={t14} alt={true} />} />;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const T0 = FlexibleSection;
  let t16;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      ...FSmeta,
      anchor: "intro",
      layoutRatio: "2-1",
      overlap: true
    };
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  const T1 = BasicContentSolo;
  const t17 = 1;
  const t18 = "grow";
  let t19;
  if ($[35] !== location) {
    t19 = formatEventDateRange(location, false, "event");
    $[35] = location;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== t19) {
    t20 = <Text prominence={Prominence.Potent} size={Sizes.XLarge} variant={Variants.Label}>{t19}</Text>;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== location.status || $[40] !== t) {
    t21 = location.status !== "open" && <Badge label={t(`volunteers:badge.${location.status}`)} color={location.status === "urgent" ? "warning" : "positive"} size="md" />;
    $[39] = location.status;
    $[40] = t;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== t20 || $[43] !== t21) {
    t22 = <>{t20}{t21}</>;
    $[42] = t20;
    $[43] = t21;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  const t23 = location.locationName;
  const t24 = addressLines.length > 0 ? <p>{addressLines.map(line => <span key={line}>{line !== addressLines[0] && <br />}{line === addressLines[0] && mapsUrl ? <LinkStyle inline={true} noUnderline={true} size="xl" weightOverride="normal"><Link href={mapsUrl}>{line}</Link></LinkStyle> : line}</span>)}</p> : <p>{t("volunteers:locationTbd")}</p>;
  const t25 = location.locationName;
  let t26;
  if ($[45] !== location || $[46] !== t) {
    t26 = buildShareSummary(t, {
      location
    });
    $[45] = location;
    $[46] = t;
    $[47] = t26;
  } else {
    t26 = $[47];
  }
  let t27;
  if ($[48] !== t) {
    t27 = t("volunteers:shareEvent");
    $[48] = t;
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  let t28;
  if ($[50] !== location.locationName || $[51] !== t26 || $[52] !== t27) {
    t28 = <ShareButtons shareTitle={t25} shareSummary={t26} size="lg" buttonLabel={t27} />;
    $[50] = location.locationName;
    $[51] = t26;
    $[52] = t27;
    $[53] = t28;
  } else {
    t28 = $[53];
  }
  let t29;
  if ($[54] !== mapsUrl || $[55] !== t) {
    t29 = mapsUrl && <ButtonStyle variant="tertiary" iconAfter={<ArkIcon iconName="arrow-right" />}><Link href={mapsUrl}>{t("volunteers:openGoogleMaps")}</Link></ButtonStyle>;
    $[54] = mapsUrl;
    $[55] = t;
    $[56] = t29;
  } else {
    t29 = $[56];
  }
  let t30;
  if ($[57] !== registerButton || $[58] !== t28 || $[59] !== t29) {
    t30 = <>{registerButton}{t28}{t29}</>;
    $[57] = registerButton;
    $[58] = t28;
    $[59] = t29;
    $[60] = t30;
  } else {
    t30 = $[60];
  }
  let t31;
  if ($[61] !== T1 || $[62] !== location.locationName || $[63] !== t22 || $[64] !== t24 || $[65] !== t30) {
    t31 = <T1 seoLevel={t17} buttonVariant={t18} subtitle={t22} title={t23} body={t24} programmaticButtons={t30} />;
    $[61] = T1;
    $[62] = location.locationName;
    $[63] = t22;
    $[64] = t24;
    $[65] = t30;
    $[66] = t31;
  } else {
    t31 = $[66];
  }
  let t32;
  if ($[67] !== mapQuery) {
    t32 = mapQuery ? <GoogleMapEmbed query={mapQuery} apiKey={NEXT_PUBLIC_GOOGLE_MAPS_KEY} /> : undefined;
    $[67] = mapQuery;
    $[68] = t32;
  } else {
    t32 = $[68];
  }
  let t33;
  if ($[69] !== T0 || $[70] !== t16 || $[71] !== t31 || $[72] !== t32) {
    t33 = <T0 meta={t16} children1={t31} children2={t32} />;
    $[69] = T0;
    $[70] = t16;
    $[71] = t31;
    $[72] = t32;
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  let t34;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = {
      ...FSmeta,
      anchor: "details",
      overlap: true
    };
    $[74] = t34;
  } else {
    t34 = $[74];
  }
  let t35;
  if ($[75] !== t) {
    t35 = t("volunteers:eventDetails");
    $[75] = t;
    $[76] = t35;
  } else {
    t35 = $[76];
  }
  let t36;
  if ($[77] !== location.description) {
    t36 = <p className="mb-comfy-md">{location.description}</p>;
    $[77] = location.description;
    $[78] = t36;
  } else {
    t36 = $[78];
  }
  let t37;
  if ($[79] !== location.dressCode || $[80] !== t) {
    t37 = location.dressCode && <div className="flex gap-comfy-xs items-start"><ArkIcon iconName="glasses-02" size="24" className="VolunteerLocation__icon shrink-0" /><div><Text prominence={Prominence.Potent} variant={Variants.Label} size={Sizes.XLarge}>{t("volunteers:dressCode")}:</Text>{" "}<Text variant={Variants.Body} size={Sizes.Medium}>{location.dressCode}</Text></div></div>;
    $[79] = location.dressCode;
    $[80] = t;
    $[81] = t37;
  } else {
    t37 = $[81];
  }
  let t38;
  if ($[82] !== location.specialEventDetails || $[83] !== t) {
    t38 = location.specialEventDetails && <div className="flex gap-comfy-xs items-start"><ArkIcon iconName="building-01" size="24" className="VolunteerLocation__icon shrink-0" /><div><Text prominence={Prominence.Potent} variant={Variants.Label} size={Sizes.XLarge}>{t("volunteers:additionalInformation")}:</Text>{" "}<Text variant={Variants.Body} size={Sizes.Medium}>{location.specialEventDetails}</Text></div></div>;
    $[82] = location.specialEventDetails;
    $[83] = t;
    $[84] = t38;
  } else {
    t38 = $[84];
  }
  let t39;
  if ($[85] !== location.locationName || $[86] !== location.volunteerCoordinatorEmail || $[87] !== location.volunteerCoordinatorName || $[88] !== t) {
    t39 = location.volunteerCoordinatorName && <div className="flex gap-comfy-xs items-start"><ArkIcon iconName="mail-01" size="24" className="VolunteerLocation__icon shrink-0" /><div><Text prominence={Prominence.Potent} variant={Variants.Label} size={Sizes.XLarge}>{t("volunteers:confirmation.volunteerCoordinator")}:</Text>{" "}<Text variant={Variants.Body} size={Sizes.Medium}>{location.volunteerCoordinatorName}{location.volunteerCoordinatorEmail && <>{" ("}<a href={buildCoordinatorMailtoUrl(location.volunteerCoordinatorEmail, location.locationName)}>{location.volunteerCoordinatorEmail}</a>{")"}</>}</Text></div></div>;
    $[85] = location.locationName;
    $[86] = location.volunteerCoordinatorEmail;
    $[87] = location.volunteerCoordinatorName;
    $[88] = t;
    $[89] = t39;
  } else {
    t39 = $[89];
  }
  let t40;
  if ($[90] !== openSlots || $[91] !== t) {
    t40 = openSlots > 0 && <div className="flex gap-comfy-xs items-start"><ArkIcon iconName="volunteer" size="24" className="VolunteerLocation__icon shrink-0" /><div><Text prominence={Prominence.Potent} variant={Variants.Label} size={Sizes.XLarge}>{t("volunteers:volunteersNeeded")}:</Text>{" "}<Text variant={Variants.Body} size={Sizes.Medium}>{openSlots}</Text></div></div>;
    $[90] = openSlots;
    $[91] = t;
    $[92] = t40;
  } else {
    t40 = $[92];
  }
  let t41;
  if ($[93] !== t36 || $[94] !== t37 || $[95] !== t38 || $[96] !== t39 || $[97] !== t40) {
    t41 = <div className="flex flex-col gap-comfy-md">{t36}{t37}{t38}{t39}{t40}</div>;
    $[93] = t36;
    $[94] = t37;
    $[95] = t38;
    $[96] = t39;
    $[97] = t40;
    $[98] = t41;
  } else {
    t41 = $[98];
  }
  let t42;
  if ($[99] !== t35 || $[100] !== t41) {
    t42 = <FlexibleSection meta={t34} children1={<BasicContentSolo seoLevel={2} title={t35} body={t41} />} />;
    $[99] = t35;
    $[100] = t41;
    $[101] = t42;
  } else {
    t42 = $[101];
  }
  let t43;
  if ($[102] !== content?.roles?.items?.[0]?.media || $[103] !== enrichedRoles || $[104] !== t) {
    t43 = <FlexibleSection meta={FSmetaFullBleedLight} children1={enrichedRoles?.items && enrichedRoles.items.length > 0 ? <ExpandedContentTabbed seoLevel={2} {...enrichedRoles} /> : <ExpandedContentTabbed seoLevel={2} {...enrichedRoles} programmaticButtons={undefined} items={[{
      title: t("volunteers:noRolesAvailable.title"),
      body: <p>{t("volunteers:noRolesAvailable.description")}</p>,
      media: content?.roles?.items?.[0]?.media
    }]} />} />;
    $[102] = content?.roles?.items?.[0]?.media;
    $[103] = enrichedRoles;
    $[104] = t;
    $[105] = t43;
  } else {
    t43 = $[105];
  }
  const t44 = content?.faq;
  let t45;
  if ($[106] !== t44) {
    t45 = <FlexibleSection meta={FSmeta} children1={<AccordionMain seoLevel={2} {...t44} />} />;
    $[106] = t44;
    $[107] = t45;
  } else {
    t45 = $[107];
  }
  let t46;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = {
      ...FSmetaLight,
      overlap: true
    };
    $[108] = t46;
  } else {
    t46 = $[108];
  }
  const t47 = content?.questions;
  let t48;
  if ($[109] !== t47) {
    t48 = <FlexibleSection meta={t46} children1={<BasicContentSolo alt={true} seoLevel={3} {...t47} />} />;
    $[109] = t47;
    $[110] = t48;
  } else {
    t48 = $[110];
  }
  let t49;
  if ($[111] !== t12 || $[112] !== t15 || $[113] !== t33 || $[114] !== t42 || $[115] !== t43 || $[116] !== t45 || $[117] !== t48) {
    t49 = <div className={t11}>{t12}{t15}{t33}{t42}{t43}{t45}{t48}</div>;
    $[111] = t12;
    $[112] = t15;
    $[113] = t33;
    $[114] = t42;
    $[115] = t43;
    $[116] = t45;
    $[117] = t48;
    $[118] = t49;
  } else {
    t49 = $[118];
  }
  return t49;
};